import styled, { css } from 'styled-components'
import {
  TypographyProps,
  TypographyVariant,
  TypographySize,
} from './Typography.types'

// Enhanced typography scales
const TYPOGRAPHY_SIZES = {
  xs: '0.75rem', // 12px
  sm: '0.875rem', // 14px
  md: '1rem', // 16px
  lg: '1.125rem', // 18px
  xl: '1.25rem', // 20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
}

/**
 * Responsive sizes - mobile-first approach
 * Base sizes for mobile (< 640px), larger sizes for tablet+ (≥ 640px)
 * Aligned with theme.breakpoints.mobile
 */
const RESPONSIVE_SIZES = {
  xs: { base: '0.75rem', sm: '0.8125rem' }, // 12px -> 13px
  sm: { base: '0.875rem', sm: '0.9375rem' }, // 14px -> 15px
  md: { base: '1rem', sm: '1.125rem' }, // 16px -> 18px
  lg: { base: '1.125rem', sm: '1.25rem' }, // 18px -> 20px
  xl: { base: '1.25rem', sm: '1.375rem' }, // 20px -> 22px
  '2xl': { base: '1.5rem', sm: '1.75rem' }, // 24px -> 28px
  '3xl': { base: '1.875rem', sm: '2.25rem' }, // 30px -> 36px
  '4xl': { base: '2.25rem', sm: '3rem' }, // 36px -> 48px
}

/**
 * Responsive heading styles for mobile-first approach
 * Ensures heading hierarchy is maintained on mobile with scaled-down sizes
 */
const RESPONSIVE_HEADING_STYLES = {
  display: { base: '2rem', sm: '2.25rem' }, // 32px -> 36px
  h1: { base: '1.75rem', sm: '1.875rem' }, // 28px -> 30px
  h2: { base: '1.375rem', sm: '1.5rem' }, // 22px -> 24px
  h3: { base: '1.125rem', sm: '1.25rem' }, // 18px -> 20px
  h4: { base: '1rem', sm: '1.125rem' }, // 16px -> 18px
}

interface VariantStyle {
  fontSize: string
  fontWeight: number
  lineHeight: number
  letterSpacing?: string
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase'
}

const VARIANT_STYLES: Record<TypographyVariant, VariantStyle> = {
  display: {
    fontSize: TYPOGRAPHY_SIZES['4xl'],
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  h1: {
    fontSize: TYPOGRAPHY_SIZES['3xl'],
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontSize: TYPOGRAPHY_SIZES['2xl'],
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: TYPOGRAPHY_SIZES.xl,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h4: {
    fontSize: TYPOGRAPHY_SIZES.lg,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: TYPOGRAPHY_SIZES.md,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: TYPOGRAPHY_SIZES.sm,
    fontWeight: 600,
    lineHeight: 1.4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  subtitle1: {
    fontSize: TYPOGRAPHY_SIZES.lg,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: TYPOGRAPHY_SIZES.md,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: TYPOGRAPHY_SIZES.md,
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: TYPOGRAPHY_SIZES.sm,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  button: {
    fontSize: TYPOGRAPHY_SIZES.sm,
    fontWeight: 500,
    lineHeight: 1.2,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: TYPOGRAPHY_SIZES.xs,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  overline: {
    fontSize: TYPOGRAPHY_SIZES.xs,
    fontWeight: 500,
    lineHeight: 1.2,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  label: {
    fontSize: TYPOGRAPHY_SIZES.xs,
    fontWeight: 500,
    lineHeight: 1.2,
  },
}

const VARIANT_ELEMENTS = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  button: 'span',
  caption: 'span',
  overline: 'span',
  label: 'label',
}

const getVariantStyles = (variant: TypographyVariant = 'body1') => {
  return VARIANT_STYLES[variant] || VARIANT_STYLES.body1
}

const getSizeStyles = (size?: TypographySize, responsive?: boolean) => {
  if (!size) return {}

  if (responsive && RESPONSIVE_SIZES[size]) {
    const sizes = RESPONSIVE_SIZES[size]
    return css`
      font-size: ${sizes.base};

      /* Tablet and up (theme.breakpoints.mobile = 640px) */
      @media (min-width: 640px) {
        font-size: ${sizes.sm};
      }
    `
  }

  return css`
    font-size: ${TYPOGRAPHY_SIZES[size]};
  `
}

/**
 * Get responsive heading styles for mobile-first approach
 * Scales down headings on mobile to maintain readability
 */
const getResponsiveHeadingStyles = (variant: TypographyVariant) => {
  const responsiveVariant =
    RESPONSIVE_HEADING_STYLES[variant as keyof typeof RESPONSIVE_HEADING_STYLES]
  if (!responsiveVariant) return ''

  return css`
    font-size: ${responsiveVariant.base};

    /* Tablet and up (theme.breakpoints.mobile = 640px) */
    @media (min-width: 640px) {
      font-size: ${responsiveVariant.sm};
    }
  `
}

export const StyledTypography = styled.span.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'variant',
      'size',
      'responsive',
      'noWrap',
      'paragraph',
      'gutterBottom',
    ].includes(prop),
})<TypographyProps>`
  margin: 0;

  ${({ variant }) => {
    const styles = getVariantStyles(variant)
    return css`
      font-size: ${styles.fontSize};
      font-weight: ${styles.fontWeight};
      line-height: ${styles.lineHeight};
      ${styles.letterSpacing && `letter-spacing: ${styles.letterSpacing};`}
      ${styles.textTransform && `text-transform: ${styles.textTransform};`}
    `
  }}

  /* Apply responsive heading styles on mobile */
  ${({ variant, responsive }) =>
    responsive && variant && getResponsiveHeadingStyles(variant)}

  ${({ size, responsive }) => getSizeStyles(size, responsive)}
  
  ${({ noWrap }) =>
    noWrap &&
    css`
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}
  
  ${({ paragraph }) =>
    paragraph &&
    css`
      margin-bottom: 1em;
    `}
  
  ${({ gutterBottom }) =>
    gutterBottom &&
    css`
      margin-bottom: 0.75em;
    `}
`

export const getVariantElement = (
  variant: TypographyVariant = 'body1',
  component?: React.ElementType
): React.ElementType => {
  if (component) return component
  return (VARIANT_ELEMENTS[variant] || 'span') as React.ElementType
}
